import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { Exclude, Expose, Type } from 'class-transformer';
import { SpConnectChangeTypeEnum } from '../../sp/connect/enums/sp-connect-change-type.enum';
import { SpConnectOrderTypeEnum } from '../../sp/connect/enums/sp-connect-order-type.enum';
import { SpConnectPurposeEnum } from '../../sp/connect/enums/sp-connect-purpose.enum';
import { JobOrderInterface } from '../interfaces/job-order.interface';

@Exclude()
export class JobOrderDto implements JobOrderInterface {
  @ApiProperty({
    title: 'SP Order ID (synonym for React Admin only)',
  })
  @Expose()
  id!: JobOrderInterface['id'];

  @ApiProperty({
    type: 'date-time',
    title: 'Created At',
  })
  @Expose()
  @Type(() => Date)
  spOrderCreatedAt!: JobOrderInterface['spOrderCreatedAt'];

  @ApiProperty({
    type: 'date-time',
    title: 'Updated At',
  })
  @Expose()
  @Type(() => Date)
  spOrderUpdatedAt!: JobOrderInterface['spOrderUpdatedAt'];

  @ApiProperty({
    title: 'SP Order ID',
  })
  @Expose()
  spOrderId!: JobOrderInterface['spOrderId'];

  @ApiProperty({ title: 'SP Order Builder Order Number', type: 'string' })
  @Expose()
  spOrderBuilderOrderNumber!: string;

  @ApiProperty({
    title: 'SP Order Issue Date',
    type: 'string',
    format: 'date-time',
  })
  @Expose()
  spOrderIssueDate!: Date;

  @ApiPropertyOptional({
    title: 'SP Order Start Date',
    type: 'string',
    format: 'date-time',
    nullable: true,
  })
  @Expose()
  spOrderStartDate!: Date;

  @ApiPropertyOptional({
    title: 'SP Order End Date',
    type: 'string',
    format: 'date-time',
    nullable: true,
  })
  @Expose()
  spOrderEndDate!: Date;

  @ApiProperty({
    title: 'SP Order: Order Id',
  })
  @Expose()
  spOrderOrderId!: JobOrderInterface['spOrderOrderId'];

  @ApiProperty({
    title: 'SP Order Purpose',
    enum: SpConnectPurposeEnum,
  })
  @Expose()
  spOrderPurpose!: JobOrderInterface['spOrderPurpose'];

  @ApiProperty({
    title: 'SP Order Order Type',
    enum: SpConnectOrderTypeEnum,
  })
  @Expose()
  spOrderOrderType!: JobOrderInterface['spOrderOrderType'];

  @ApiProperty({
    title: 'SP Order Change Type',
    type: 'enum',
    enum: SpConnectChangeTypeEnum,
  })
  @Expose()
  spOrderChangeType!: SpConnectChangeTypeEnum;

  @ApiProperty({
    title: 'SP Order Change Order Issue Date',
    type: 'string',
    format: 'date-time',
  })
  @Expose()
  spOrderChangeOrderIssueDate!: Date;

  @ApiProperty({ title: 'SP Order Change Order Number', type: 'string' })
  @Expose()
  spOrderChangeOrderNumber!: string;

  @ApiProperty({ title: 'SP Order Change Order Sequence', type: 'number' })
  @Expose()
  spOrderChangeOrderSequence!: number;

  @ApiProperty({
    title: 'SP Order Builder Name',
    nullable: true,
  })
  @Expose()
  spOrderBuilderName!: JobOrderInterface['spOrderBuilderName'];

  @ApiProperty({
    title: 'SP Order Subdivision Name',
    nullable: true,
  })
  @Expose()
  spOrderSubdivisionName!: JobOrderInterface['spOrderSubdivisionName'];

  @ApiProperty({
    title: 'SP Order Job Plan',
    nullable: true,
  })
  @Expose()
  spOrderJobPlan!: JobOrderInterface['spOrderJobPlan'];

  @ApiProperty({
    title: 'SP Order Job Group Descritption',
    nullable: true,
  })
  @Expose()
  spOrderJobGroupDescription!:
    | JobOrderInterface['spOrderJobGroupDescription']
    | null;

  @ApiProperty({
    title: 'SP Order Job Lot',
    nullable: true,
  })
  @Expose()
  spOrderJobLot!: JobOrderInterface['spOrderJobLot'];

  @ApiPropertyOptional({
    title: 'SP Order Job Block',
    nullable: true,
  })
  @Expose()
  spOrderJobBlock!: JobOrderInterface['spOrderJobBlock'];

  @ApiProperty({
    title: 'SP Order Job Street',
    nullable: true,
  })
  @Expose()
  spOrderJobStreet!: JobOrderInterface['spOrderJobStreet'];

  @ApiProperty({
    title: 'SP Order Job Street Supplement',
    nullable: true,
  })
  @Expose()
  spOrderJobStreetSupplement!: JobOrderInterface['spOrderJobStreetSupplement'];

  @ApiProperty({
    title: 'SP Order Job City',
    nullable: true,
  })
  @Expose()
  spOrderJobCity!: JobOrderInterface['spOrderJobCity'];

  @ApiProperty({
    title: 'SP Order Job State Code',
    nullable: true,
  })
  @Expose()
  spOrderJobStateCode!: JobOrderInterface['spOrderJobStateCode'];

  @ApiProperty({
    title: 'SP Order Job Postal Code',
    nullable: true,
  })
  @Expose()
  spOrderJobPostalCode!: JobOrderInterface['spOrderJobPostalCode'];

  @ApiProperty({
    title: 'SP Builder Name ID',
    nullable: true,
  })
  @Expose()
  spOrderBuilderNameId!: JobOrderInterface['spOrderBuilderNameId'];

  @ApiProperty({
    title: 'SP Subdivision Name ID',
    nullable: true,
  })
  @Expose()
  spOrderSubdivisionNameId!: JobOrderInterface['spOrderSubdivisionNameId'];

  @ApiProperty({
    title: 'SP Job Plan ID',
    nullable: true,
  })
  @Expose()
  spOrderJobPlanId!: JobOrderInterface['spOrderJobPlanId'];

  @ApiProperty({
    title: 'SP Summary Order Total',
    type: 'number',
    nullable: true,
  })
  @Expose()
  spOrderSummaryOrderTotal!: JobOrderInterface['spOrderSummaryOrderTotal'];

  @ApiProperty({
    title: 'Builder Id',
    nullable: true,
  })
  @Expose()
  builderId!: JobOrderInterface['builderId'];

  @ApiProperty({
    title: 'Builder Name',
    nullable: true,
  })
  @Expose()
  builderName!: JobOrderInterface['builderName'];

  @ApiProperty({
    title: 'Subdivision Id',
    nullable: true,
  })
  @Expose()
  subdivisionId!: JobOrderInterface['subdivisionId'];

  @ApiProperty({
    title: 'Subdivision Name',
    nullable: true,
  })
  @Expose()
  subdivisionName!: JobOrderInterface['subdivisionName'];

  @ApiProperty({
    title: 'Model Id',
    nullable: true,
  })
  @Expose()
  modelId!: JobOrderInterface['modelId'];

  @ApiProperty({
    title: 'Model Name',
    nullable: true,
  })
  @Expose()
  modelName!: JobOrderInterface['modelName'];

  @ApiProperty({
    type: 'boolean',
    title: 'Imported Status',
  })
  @Expose()
  imported!: JobOrderInterface['imported'];

  @ApiProperty({
    type: 'number',
    title: 'Order Sibling Count',
  })
  @Expose()
  orderSiblingCount!: JobOrderInterface['orderSiblingCount'];

  @ApiProperty({
    type: 'boolean',
    title: 'Is New?',
  })
  @Expose()
  isNew!: JobOrderInterface['isNew'];

  @ApiProperty({
    type: 'boolean',
    title: 'Is Duplicate?',
  })
  @Expose()
  isDuplicate!: JobOrderInterface['isDuplicate'];

  @ApiProperty({
    title: 'Job Id',
    nullable: true,
  })
  @Expose()
  jobId!: JobOrderInterface['jobId'];
}
